/**
 * @file Today's View — /hoy
 */
import React, { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Sun } from "lucide-react";
import { useAppointments } from "../hooks/useAppointments";
import { APPOINTMENT_STRINGS } from "../constants/strings";
import { Button, Card, Chip, Spinner } from "../ui";
import "./TodayView.css";

const STATUS_TONES = {
  AGENDADA: "warn",
  CONFIRMADA: "ok",
  COMPLETADA: "primary",
  CANCELADA: "neutral",
  NO_ASISTIO: "err",
};

const timeStr = (dt) =>
  new Date(dt).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });

export default function TodayView() {
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const { appointments, isLoading } = useAppointments({
    filters: { dateFrom: today, dateTo: today },
  });

  const sorted = useMemo(
    () => [...appointments].sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime)),
    [appointments]
  );

  return (
    <div className="td-page">
      <header className="td-head">
        <Sun size={20} className="td-head-icon" />
        <div>
          <span className="td-eyebrow">Agenda de hoy</span>
          <span className="td-date">
            {new Date().toLocaleDateString("es-MX", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </span>
        </div>
      </header>

      {isLoading ? (
        <div className="td-loading">
          <Spinner size={28} />
        </div>
      ) : sorted.length === 0 ? (
        <Card className="td-empty">
          <p>No hay citas programadas para hoy.</p>
        </Card>
      ) : (
        <div className="td-list">
          {sorted.map((appt) => {
            const isPast = new Date(appt.dateTime) < now;
            const tone = STATUS_TONES[appt.status] || "neutral";
            return (
              <Card key={appt.id} className={`td-item td-item-${tone} ${isPast ? "td-item-past" : ""}`}>
                <div className="td-item-row">
                  <div className="td-item-meta">
                    <span className="td-time">
                      {timeStr(appt.dateTime)} a {timeStr(appt.endTime)}
                    </span>
                    <span className="td-name">
                      {appt.patient.user.firstName} {appt.patient.user.lastName}
                      {appt.attendeeName && (
                        <span className="td-attendee"> (para: {appt.attendeeName})</span>
                      )}
                    </span>
                    {appt.reason && <span className="td-reason">{appt.reason}</span>}
                  </div>
                  <div className="td-item-actions">
                    <Chip tone={tone} size="sm">
                      {APPOINTMENT_STRINGS.STATUS_LABELS[appt.status]}
                    </Chip>
                    <Button asChild variant="outline" size="sm">
                      <RouterLink to={`/cita/${appt.id}`}>Ver detalle</RouterLink>
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
          <p className="td-count">
            {sorted.length} cita{sorted.length !== 1 ? "s" : ""} hoy
          </p>
        </div>
      )}
    </div>
  );
}
